import { type PublicBotInfo, useIM } from '@wui/im'

import { Contact } from '@/shared/types/api'

import {
  getBotPServedFromSearchQuery,
  isBotCatalogSearchQuery,
  matchesPublicBotSearch,
  parseBotSearchToken,
  publicBotToContact,
  resolvePublicBotPServed,
  userBotToContact,
} from './bot-search'
import { findMatchingSystemBotContacts, getSystemBotContact } from './system-bots'

let publicBotsCache: PublicBotInfo[] | null = null
let publicBotsRequest: Promise<PublicBotInfo[]> | null = null

export const loadPublicBots = async (): Promise<PublicBotInfo[]> => {
  if (publicBotsCache) {
    return publicBotsCache
  }
  if (publicBotsRequest) {
    return publicBotsRequest
  }

  publicBotsRequest = useIM().requestPublicBots()
    .then((bots) => {
      publicBotsCache = bots
      return bots
    })
    .catch((e) => {
      console.error(e)
      return [] as PublicBotInfo[]
    })
    .finally(() => {
      publicBotsRequest = null
    })

  return publicBotsRequest
}

const findCatalogBotByPServed = (bots: PublicBotInfo[], pServed: string): PublicBotInfo | undefined => {
  return bots.find((bot) => {
    const botPServed = resolvePublicBotPServed(bot)
    return !!botPServed && botPServed.toLowerCase() === pServed.toLowerCase()
  })
}

export const resolveBotDisplayName = async (pServed: string, fallback: string): Promise<string> => {
  const systemBot = getSystemBotContact(pServed)
  if (systemBot?.name) {
    return systemBot.name
  }

  try {
    const catalog = await loadPublicBots()
    const catalogBot = findCatalogBotByPServed(catalog, pServed)
    if (catalogBot?.name) {
      return catalogBot.name
    }
  } catch (e) {
    console.error(e)
  }

  try {
    const brief = await useIM().requestBriefStatus({ contactId: pServed })
    if (brief?.name) {
      return brief.name
    }
  } catch (e) {
    console.error(e)
  }

  return fallback
}

const addUniqueBot = (result: Contact[], seen: Set<string>, contact: Contact) => {
  const key = contact.imLogin.toLowerCase()
  if (seen.has(key)) {
    return
  }

  seen.add(key)
  result.push(contact)
}

export const searchBotsForQuery = async (query: string): Promise<Contact[]> => {
  if (!isBotCatalogSearchQuery(query)) {
    return []
  }

  const catalog = await loadPublicBots()
  const result: Contact[] = []
  const seen = new Set<string>()

  findMatchingSystemBotContacts(query).forEach((contact) => addUniqueBot(result, seen, contact))

  catalog
    .filter(bot => matchesPublicBotSearch(query, bot))
    .forEach((bot) => {
      const pServed = resolvePublicBotPServed(bot)
      if (!pServed) {
        return
      }

      addUniqueBot(result, seen, publicBotToContact(bot, pServed))
    })

  const botPServed = getBotPServedFromSearchQuery(query)
  if (!botPServed || seen.has(botPServed.toLowerCase())) {
    return result
  }

  const systemBot = getSystemBotContact(botPServed)
  if (systemBot) {
    addUniqueBot(result, seen, systemBot)
    return result
  }

  const catalogBot = findCatalogBotByPServed(catalog, botPServed)
  if (catalogBot) {
    addUniqueBot(result, seen, publicBotToContact(catalogBot, botPServed))
    return result
  }

  const accepted = await useIM().subscribeOnConferenceUpdates({ pServed: botPServed })
  if (!accepted) {
    return result
  }

  const token = parseBotSearchToken(query)
  const name = await resolveBotDisplayName(botPServed, token ?? botPServed)
  addUniqueBot(result, seen, userBotToContact(botPServed, name))

  return result
}
